<script setup lang="ts">
import PdsBadge from '@/components/pds/PdsBadge.vue';
import PdsButton from '@/components/pds/PdsButton.vue';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import EmptyState from '../components/EmptyState.vue';
import CancelledModal from '../components/CancelledModal.vue';
import InfoCard from '../components/InfoCard.vue';
import InfoRow from '../components/InfoRow.vue';
import ItemRow from '../components/ItemRow.vue';
import OrderPrintModal from '../components/OrderPrintModal.vue';
import PageBar from '../components/PageBar.vue';
import AcceptOrderModal from '../components/AcceptOrderModal.vue';
import CancelOrderModal from '../components/CancelOrderModal.vue';
import { formatDateTime, formatFullDateTime, formatMoney } from '../format';
import { usePdsOrdersStore } from '../stores/orders.store';
import { itemChanges } from '../itemChanges';
import { backTarget } from '../orderLink';
import { resolveGateway } from '../resolve-gateway';
import type { PdsModification, PdsOrder, PdsRefusal } from '../types';

const store = usePdsOrdersStore();
const route = useRoute();
const router = useRouter();

const orderId = computed(() => String(route.params.id));
const boardOrder = computed(() => store.byId(orderId.value));
// Une commande hors du tableau (remise, annulée), ouverte depuis la recherche : lue seule, jamais écrite dans le store.
const pastOrder = ref<PdsOrder | null>(null);
const order = computed(() => boardOrder.value ?? pastOrder.value ?? undefined);
const refusing = ref(false);
const accepting = ref(false);
const printing = ref(false);

const toPrepare = computed(
  () => order.value?.items.filter((i) => !i.prepared) ?? [],
);
const prepared = computed(
  () => order.value?.items.filter((i) => i.prepared) ?? [],
);
const removed = computed(() => order.value?.removedItems ?? []);

// L'historique des modifications se relit à l'ouverture, puis chaque fois que les lignes de la commande changent
// (modification faite d'ici ou d'un autre poste) : la quantité en moins et le motif y sont gardés par le serveur.
const modifications = ref<PdsModification[]>([]);
const linesSignature = computed(
  () =>
    order.value?.items.map((i) => `${i.id}:${i.quantity}`).join(',') +
    `|${removed.value.length}`,
);
// Pour chaque produit : ce que les modifications lui ont fait (quantité en moins, remboursement, motif).
const changes = computed(() =>
  itemChanges(
    [...(order.value?.items ?? []), ...removed.value],
    modifications.value,
  ),
);

watch(
  [orderId, linesSignature],
  async () => {
    const id = orderId.value;
    const found = await resolveGateway()
      .loadModifications(id)
      .catch((): PdsModification[] => []);
    if (id === orderId.value) modifications.value = found;
  },
  { immediate: true },
);
const remaining = computed(() => toPrepare.value.length);
// Remise, annulée ou refusée : on la lit, on ne la travaille plus.
const finished = computed(
  () =>
    order.value?.stage === 'handed' ||
    order.value?.stage === 'cancelled' ||
    order.value?.stage === 'refused',
);
const finishedAt = computed(() =>
  order.value?.stage === 'handed'
    ? order.value.handedAt
    : order.value?.cancelledAt,
);
const late = computed(() => !!order.value && store.isLate(order.value));
const mustAcknowledge = computed(
  () =>
    boardOrder.value?.stage === 'cancelled' &&
    !boardOrder.value.cancelAcknowledged,
);

const goBoard = () => router.push('/');
// Ouverte depuis le journal ou la recherche : « retour » ramène là, pas au tableau.
const back = computed(() => backTarget(route.query.retour));

// Refuser, remettre ou acquitter une annulation depuis cet écran le ferme. Arriver sur une commande déjà finie
// (le « Voir » d'une notification) l'ouvre en lecture : on ne la renvoie au tableau que si elle change sous nos yeux.
watch(
  () =>
    [boardOrder.value?.stage, boardOrder.value?.cancelAcknowledged] as const,
  ([stage, acknowledged], [previous]) => {
    if (!previous || store.loadStatus !== 'ready') return;
    const gone =
      stage === 'refused' ||
      stage === 'handed' ||
      (stage === 'cancelled' && acknowledged);
    if (gone) goBoard();
  },
);

// Hors du tableau, on la lit sur le serveur ; introuvable, on revient au tableau.
watch(
  [() => store.loadStatus, orderId, boardOrder],
  async ([status, id, onBoard]) => {
    if (status !== 'ready' || onBoard) return;
    pastOrder.value = null;
    const found = await resolveGateway()
      .loadOrder(id)
      .catch((): null => null);
    if (id !== orderId.value) return;
    if (found) pastOrder.value = found;
    else goBoard();
  },
  { immediate: true },
);

// Les liens de l'e-mail ouvrent la fiche avec la fenêtre de refus ou de confirmation : un clic dans un mail ne valide
// jamais une commande tout seul, il faut un second geste.
const refuseLinks = ['reject', 'rejected', 'refuse', 'refused'];
const acceptLinks = ['accept', 'accepted'];
watch(
  () => [order.value?.stage, route.query.action] as const,
  ([stage, action]) => {
    if (typeof action !== 'string') return;
    if (!stage) return;
    if (stage === 'new') {
      refusing.value = refuseLinks.includes(action.toLowerCase());
      accepting.value = acceptLinks.includes(action.toLowerCase());
    }
    const { action: _consumed, ...query } = route.query;
    void router.replace({ query });
  },
  { immediate: true },
);

// Accepter ou marquer prête garde la fiche ouverte : l'écran passe à l'étape suivante (préparation, prête) sur place.
const accept = () => store.accept(order.value!.id);

const refuse = (refusal: PdsRefusal) => {
  store.refuse(order.value!.id, refusal);
  refusing.value = false;
};

const handOver = () => store.handOver(order.value!.id);
</script>

<template>
  <template v-if="order">
    <PageBar tall :back-to="back?.to" :back-label="back?.label">
      <h1
        class="text-[28px] leading-none font-bold tracking-[-0.4px] sm:text-[32px]"
      >
        #{{ order.orderNumber ?? order.number }}
      </h1>
      <p class="text-encre-douce min-w-0 truncate text-[14px] leading-[1.4]">
        {{ order.label }}
      </p>
      <PdsBadge v-if="late" tone="late">Retard</PdsBadge>
      <PdsBadge v-else-if="order.stage === 'new'" tone="new">Nouvelle</PdsBadge>
      <PdsBadge v-else-if="order.stage === 'handed'" tone="done">
        Remise
      </PdsBadge>
      <PdsBadge v-else-if="finished" tone="closed">
        {{ order.stage === 'refused' ? 'Refusée' : 'Annulée' }} · remboursée
      </PdsBadge>
      <PdsBadge v-else-if="order.stage === 'preparing'" tone="preparing">
        En préparation
      </PdsBadge>
    </PageBar>

    <main
      class="no-scrollbar grid min-h-0 flex-1 grid-cols-1 items-start gap-5 overflow-y-auto px-4 pt-[18px] pb-4 sm:px-5 sm:pb-5"
      :class="
        order.stage === 'new' || finished
          ? 'lg:grid-cols-[1fr_340px]'
          : 'lg:grid-cols-[1fr_1fr_340px]'
      "
    >
      <section
        v-if="order.stage === 'new' || finished"
        class="flex flex-col gap-[9px] pt-[10px]"
      >
        <h2
          class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
        >
          {{ finished ? 'Produits' : 'Contenu de la commande' }}
          <span class="text-encre ml-2">{{ order.items.length }}</span>
        </h2>
        <div class="bg-carte rounded-[32px] px-4 pt-[15px] pb-3">
          <ItemRow v-for="item in order.items" :key="item.id" :item="item" />
        </div>
      </section>

      <template v-else>
        <section class="flex flex-col gap-[9px] pt-[10px]">
          <h2
            class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
          >
            À préparer
            <span class="text-encre ml-2">{{ toPrepare.length }}</span>
          </h2>
          <div class="bg-carte rounded-[32px] px-4 pt-[15px] pb-3">
            <EmptyState
              v-if="!toPrepare.length"
              icon="circle-check"
              title="Tout est préparé"
              :text="
                order.stage === 'ready'
                  ? 'La commande est prête.'
                  : 'Vous pouvez marquer la commande prête.'
              "
            />
            <ItemRow
              v-for="item in toPrepare"
              :key="item.id"
              :item="item"
              :change="changes.get(item.id)"
              :selectable="order.stage === 'preparing'"
              @toggle="store.toggleItem(order.id, item.id)"
            />
            <ItemRow
              v-for="item in removed"
              :key="item.id"
              :item="item"
              :change="changes.get(item.id)"
              removed
            />
          </div>
        </section>
        <section class="flex flex-col gap-[9px] pt-[10px]">
          <h2
            class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
          >
            Préparés
            <span class="text-encre ml-2">{{ prepared.length }}</span>
          </h2>
          <div class="bg-carte rounded-[32px] px-4 pt-[15px] pb-3">
            <EmptyState
              v-if="!prepared.length"
              icon="box"
              title="Rien de préparé"
              text="Cochez un produit, il passera ici."
            />
            <ItemRow
              v-for="item in prepared"
              :key="item.id"
              :item="item"
              :change="changes.get(item.id)"
              compact
              :selectable="order.stage === 'preparing'"
              @toggle="store.toggleItem(order.id, item.id)"
            />
          </div>
        </section>
      </template>

      <div class="flex flex-col gap-3">
        <InfoCard
          label="Contact client"
          :value="order.contact.name ?? order.label"
        >
          <dl>
            <InfoRow v-if="order.contact.phone" label="Téléphone">
              {{ order.contact.phone }}
            </InfoRow>
            <InfoRow v-if="order.contact.email" label="Courriel">
              {{ order.contact.email }}
            </InfoRow>
            <InfoRow
              v-if="!order.contact.phone && !order.contact.email"
              label="Contact"
            >
              Aucun
            </InfoRow>
          </dl>
        </InfoCard>
        <InfoCard v-if="order.customerNote" label="Note du client">
          <p class="py-[9px] text-[14px] leading-[1.4]">
            {{ order.customerNote }}
          </p>
        </InfoCard>
        <InfoCard label="Retrait" :value="formatDateTime(order.dueAt)">
          <dl>
            <InfoRow label="Canal">{{ order.channel }}</InfoRow>
            <InfoRow label="Code">{{ order.pickupCode }}</InfoRow>
          </dl>
        </InfoCard>
        <div
          class="bg-creux flex items-center justify-between rounded-[24px] px-4 py-[14px]"
        >
          <p
            class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
          >
            {{
              order.stage === 'handed'
                ? 'Total payé'
                : finished
                  ? 'Remboursé'
                  : 'Total payé'
            }}
          </p>
          <p class="text-[18px] leading-[1.2] font-bold">
            {{ formatMoney(order.totalPaidCents) }}
          </p>
        </div>
      </div>
    </main>

    <footer
      class="bg-carte border-filet grid min-h-[94px] shrink-0 grid-cols-2 items-center gap-3 border-t px-4 py-4 sm:flex sm:flex-wrap sm:px-5"
    >
      <template v-if="order.stage === 'new'">
        <PdsButton variant="second" fit @click="refusing = true">
          Refuser
        </PdsButton>
        <span class="hidden flex-1 sm:block"></span>
        <PdsButton
          class="max-sm:order-first max-sm:col-span-2"
          fit
          @click="accept"
        >
          Accepter la commande
        </PdsButton>
      </template>
      <template v-else-if="order.stage === 'preparing'">
        <PdsButton variant="second" fit :to="`/commande/${order.id}/modifier`">
          Modifier la commande
        </PdsButton>
        <PdsButton variant="second" fit @click="printing = true">
          Imprimer
        </PdsButton>
        <span class="hidden flex-1 sm:block"></span>
        <PdsButton
          class="max-sm:order-first max-sm:col-span-2"
          fit
          @click="store.requestReady(order.id)"
        >
          Marquer prête<template v-if="remaining">
            · {{ remaining }} restant{{ remaining > 1 ? 's' : '' }}</template
          >
        </PdsButton>
      </template>
      <template v-else-if="order.stage === 'ready'">
        <span class="hidden flex-1 sm:block"></span>
        <PdsButton class="col-span-2" fit @click="handOver">
          Remettre au client
        </PdsButton>
      </template>
      <template v-else>
        <div class="col-span-2 flex flex-col gap-[2px] sm:col-span-1">
          <span
            class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
          >
            {{
              order.stage === 'handed' ? 'Commande remise' : 'Commande annulée'
            }}
          </span>
          <span
            v-if="finishedAt"
            class="text-[18px] leading-[1.2] font-semibold"
          >
            {{ formatFullDateTime(finishedAt) }}
          </span>
        </div>
        <span class="hidden flex-1 sm:block"></span>
        <template v-if="order.stage === 'handed'">
          <!-- Le serveur refuse tout remboursement d'une commande remise : le bouton attend son endpoint. -->
          <PdsButton variant="second" fit disabled>Rembourser</PdsButton>
          <PdsButton fit @click="printing = true">Imprimer le ticket</PdsButton>
        </template>
      </template>
    </footer>

    <OrderPrintModal v-if="printing" :order="order" @close="printing = false" />
    <AcceptOrderModal
      v-if="accepting"
      :order="order"
      @close="accepting = false"
      @confirm="
        accept();
        accepting = false;
      "
    />
    <CancelOrderModal
      v-if="refusing"
      kind="refuse"
      :order="order"
      @close="refusing = false"
      @confirm="refuse"
    />
    <CancelledModal
      v-if="mustAcknowledge"
      :order="order"
      @acknowledge="store.acknowledgeCancellation(order.id)"
    />
  </template>
</template>
